import React, { useEffect, useState } from "react";
import { fetchDcOuts, createDcOut } from "../../services/api/boq/dcOutApi";
import {
  fetchDcIn,
  fetchDcInItemsStock,
  getDcInDropdown,
} from "../../services/api/boq/dcInApi";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import toast from "react-hot-toast";

const DcOutList = () => {
  const [dcOuts, setDcOuts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dcIns, setDcIns] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalIssued, setTotalIssued] = useState(0);
  const [selectedDc, setSelectedDc] = useState(null);

  const [form, setForm] = useState({
    project_id: "",
    dc_in_id: "",
    issue_date: "",
    remarks: "",
    issued_to: "",
    items: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, projectFilter]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [dcOutRes, projectRes] = await Promise.all([
        fetchDcOuts(),
        fetchProjectsApi(),
      ]);

      setDcOuts(dcOutRes || []);
      setProjects(projectRes?.data?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Load DC IN
  const loadDcIns = async () => {
    try {
      const data = await getDcInDropdown();
      setDcIns(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadDcIns();
  }, []);

  // only sending projectid
  const handleProjectChange = (projectId) => {
    setForm((prev) => ({
      ...prev,
      project_id: projectId,
    }));
  };
  // const handleProjectChange = async (projectId) => {
  //     setForm({
  //         project_id: projectId,
  //         dc_in_id: "",
  //         issue_date: "",
  //         remarks: "",
  //         items: []
  //     });

  //     if (!projectId) return;

  //     try {
  //         const res = await fetchDcIn(projectId);

  //         // ✅ CORRECT STRUCTURE
  //         const dcList = res?.data?.data || [];

  //         setDcIns(dcList);

  //     } catch (err) {
  //         console.error(err);
  //         setDcIns([]);
  //     }
  // };

  const handleDcInChange = async (dcInId) => {
    setForm((prev) => ({
      ...prev,
      dc_in_id: dcInId,
      items: [],
    }));

    if (!dcInId) return;

    try {
      const res = await fetchDcInItemsStock(dcInId);

      // adjust based on API response
      const itemsList = res?.data || res || [];

      const formatted = itemsList.map((i) => ({
        boq_item_id: i.boq_item_id || i.id,
        item_name: i.item_name,
        unit: i.unit || "",
        available_qty: Number(i.available_qty || i.dcin_qty || 0),
        issued_qty: "",
        is_manual: false,
      }));

      setForm((prev) => ({
        ...prev,
        items: formatted,
      }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load items");
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];

    const item = updated[index];
    item[field] = value;

    if (field === "issued_qty") {
      const qty = Number(value);

      // ❌ BLOCK OVER ISSUE
      if (!item.is_manual && qty > item.available_qty) {
        toast.error(`Cannot exceed available (${item.available_qty})`);
        return;
      }

      item.issued_qty = qty;

      // 🔢 LIVE BALANCE
      item.remaining_qty = !item.is_manual ? item.available_qty - qty : 0;
    }

    updated[index] = item;

    setForm({ ...form, items: updated });

    // 🔥 UPDATE TOTAL
    calculateTotal(updated);
  };

  const calculateTotal = (items) => {
    const total = items.reduce((sum, i) => {
      return sum + Number(i.issued_qty || 0);
    }, 0);

    setTotalIssued(total);
  };

  const addManualItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          boq_item_id: null,
          item_name: "",
          unit: "",
          available_qty: 0,
          issued_qty: "",
          is_manual: true,
        },
      ],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.project_id) return toast.error("Select project");
    if (!form.dc_in_id) return toast.error("Select DC IN");

    for (let item of form.items) {
      if (!item.item_name) return toast.error("Item required");

      if (!item.issued_qty || item.issued_qty <= 0)
        return toast.error("Invalid qty");

      if (!item.is_manual && item.issued_qty > item.available_qty)
        return toast.error("Qty exceeds available");

      if (!form.issued_to) {
        return toast.error("Issued To is required");
      }
    }

    try {
      setLoading(true);

      const payload = {
        project_id: form.project_id,
        dc_in_id: form.dc_in_id,
        issue_date: form.issue_date,
        remarks: form.remarks,
        issued_to: form.issued_to,
        // Map the items to ensure clean data
        items: form.items.map((item) => ({
          boq_item_id: Number(item.boq_item_id),
          item_name: item.item_name,
          unit: item.unit,
          issued_qty: Number(item.issued_qty),
          is_manual: item.is_manual,
        })),
      };
      await createDcOut(payload);
      //await createDcOut(form);

      toast.success("DC OUT Created");

      setShowModal(false);
      loadData();

      // ✅ Reset form
      setForm({
        project_id: "",
        dc_in_id: "",
        issue_date: "",
        remarks: "",
        items: [],
      });
    } catch (err) {
      console.error(err);
      toast.error("Error saving");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 FILTER
  const filtered = dcOuts.filter((dc) => {
    const matchSearch =
      dc.dc_number?.toLowerCase().includes(search.toLowerCase()) ||
      dc.remarks?.toLowerCase().includes(search.toLowerCase());

    const matchProject = !projectFilter || dc.project_id == projectFilter;

    return matchSearch && matchProject;
  });

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const getProjectName = (id) => {
    const p = projects.find((p) => p.id == id);
    return p?.project_name || "-";
  };

  const removeItem = (index) => {
    const updated = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: updated });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* HEADER */}
      <div className="flex justify-between mb-4">
        <h2 className="text-xl font-semibold">DC OUT</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          + Create
        </button>
      </div>

      {/* FILTER */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          placeholder="Search"
          className="border p-2"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="border p-2"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.project_name}
            </option>
          ))}
        </select>

        <select
          className="border p-2"
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
        </select>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-gray-700">
          {/* HEADER */}
          <thead className="bg-gray-200 text-gray-800 uppercase text-xs tracking-wider">
            <tr>
              <th className="p-3 text-left">SlNo</th>
              <th className="p-3 text-left">DC No</th>
              <th className="p-3 text-left">Project</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Items</th>
              <th className="p-3 text-left">Remarks</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          {/* BODY */}
          <tbody>
            {paginated.map((dc, index) => (
              <tr
                key={dc.id}
                className="border-b hover:bg-blue-50 transition duration-200 even:bg-gray-50"
              >
                <td className="p-3 font-medium">{index + 1}</td>

                <td className="p-3 text-blue-600 font-semibold">
                  {dc.dc_number}
                </td>

                <td className="p-3">{getProjectName(dc.project_id)}</td>

                <td className="p-3 text-gray-600">{dc.issue_date}</td>

                <td className="p-3">
                  <span className="font-semibold">
                    {dc.items?.length} Items
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">
                    Qty:{" "}
                    {dc.items?.reduce(
                      (s, i) => s + Number(i.issued_qty || 0),
                      0,
                    )}
                  </span>
                </td>

                <td className="p-3 text-gray-600">{dc.issued_to}</td>

                <td className="p-3 text-center">
                  <button
                    onClick={() => setSelectedDc(dc)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs shadow"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-between mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Prev
        </button>

        <span>
          Page {currentPage} / {totalPages}
        </span>

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white w-full max-w-3xl rounded shadow-lg flex flex-col max-h-[90vh]">
            {/* HEADER */}
            <div className="p-4 border-b font-semibold">Create DC OUT</div>

            {/* BODY (SCROLLABLE) */}
            <div className="p-4 overflow-y-auto space-y-3">
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* PROJECT */}
                <select
                  className="border p-2 w-full"
                  value={form.project_id}
                  onChange={(e) => handleProjectChange(Number(e.target.value))}
                >
                  <option value="">Select Project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>

                {/* DC IN */}
                <select
                  className="border p-2 w-full"
                  value={form.dc_in_id}
                  onChange={(e) => handleDcInChange(Number(e.target.value))}
                >
                  <option value="">Select DC IN</option>

                  {dcIns.map((dc) => (
                    <option key={dc.id} value={dc.id}>
                      {dc.dc_number} ({dc.items_count} items)
                    </option>
                  ))}
                </select>

                {/* DATE */}
                <input
                  type="date"
                  className="border p-2 w-full"
                  onChange={(e) =>
                    setForm({ ...form, issue_date: e.target.value })
                  }
                />
                {/* issued to */}
                <input
                  type="text"
                  placeholder="Issued To"
                  className="border p-2 w-full"
                  value={form.issued_to}
                  onChange={(e) =>
                    setForm({ ...form, issued_to: e.target.value })
                  }
                />

                {/* REMARK */}
                <textarea
                  placeholder="Remarks"
                  className="border p-2 w-full"
                  onChange={(e) =>
                    setForm({ ...form, remarks: e.target.value })
                  }
                />

                {/* ITEMS TABLE WITH SCROLL */}
                <div className="border rounded max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr className="text-left">
                        <th className="p-2 ">SlNo</th>
                        <th className="p-2 ">Item</th>
                        <th className="p-2 ">Unit</th>
                        <th className="p-2 ">Available</th>
                        <th className="p-2 ">Balance</th>
                        <th className="p-2 ">Qty</th>
                        <th className="p-2 ">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {form.items.map((item, index) => (
                        <tr key={index} className="border-t text-left">
                          <td className="p-2">{index + 1}</td>

                          <td className="p-2">
                            {item.is_manual ? (
                              <input
                                className="border p-1 w-full"
                                placeholder="Item"
                                onChange={(e) =>
                                  handleItemChange(
                                    index,
                                    "item_name",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.item_name
                            )}
                          </td>

                          <td className="p-2">
                            {item.is_manual ? (
                              <input
                                className="border p-1 w-full"
                                placeholder="Unit"
                                onChange={(e) =>
                                  handleItemChange(
                                    index,
                                    "unit",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.unit || "-"
                            )}
                          </td>

                          <td className="p-2 text-gray-500">
                            {item.is_manual ? "-" : item.available_qty}
                          </td>

                          <td className="p-2 text-gray-600">
                            {item.is_manual
                              ? "-"
                              : item.available_qty - (item.issued_qty || 0)}
                          </td>

                          <td className="p-2">
                            <input
                              type="number"
                              className="border p-1 w-full"
                              placeholder="Qty"
                              onChange={(e) => {
                                const value = Number(e.target.value);

                                if (
                                  !item.is_manual &&
                                  value > item.available_qty
                                ) {
                                  toast.error("Exceeds available qty");
                                  return;
                                }

                                handleItemChange(index, "issued_qty", value);
                              }}
                            />
                          </td>

                          <td className="p-2">
                            {item.is_manual && (
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-600 text-xs bg-red-100 px-1 rounded px-2 py-1"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ADD MANUAL */}
                <button
                  type="button"
                  onClick={addManualItem}
                  className="text-blue-600"
                >
                  + Add Manual Item
                </button>
              </form>
            </div>

            {/* FOOTER (STICKY) */}
            <div className="p-4 border-t flex justify-end gap-2">
              <button onClick={() => setShowModal(false)}>Cancel</button>

              <button
                onClick={handleSubmit}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* view modal */}
      {selectedDc && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white w-full max-w-3xl rounded shadow-lg p-4">
            {/* Header */}
            <div className="flex justify-between border-b pb-2 mb-3">
              <h2 className="text-lg font-bold">DC OUT Details</h2>
              <button onClick={() => setSelectedDc(null)}>✕</button>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
              <p>
                <b>DC No:</b> {selectedDc.dc_number}
              </p>
              <p>
                <b>Date:</b> {selectedDc.issue_date}
              </p>
              <p>
                <b>Project:</b> {getProjectName(selectedDc.project_id)}
              </p>
              <p>
                <b>Issued To:</b> {selectedDc.issued_to}
              </p>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto overflow-y-auto max-h-[400px] border rounded-lg">
              <table className="w-full text-sm border ">
                <thead className="bg-gray-100">
                  <tr className="text-left">
                    <th className="p-2">#</th>
                    <th className="p-2">Item Name</th>
                    <th className="p-2">Qty</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedDc.items?.map((item, i) => (
                    <tr key={item.id} className="border-t">
                      <td className="p-2">{i + 1}</td>

                      {/* IMPORTANT FIX */}
                      <td className="p-2">
                        {item.item_name || item.boq_item?.item_name || "N/A"}
                      </td>

                      <td className="p-2">{item.issued_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DcOutList;
